import {useState, useEffect, useCallback, useRef} from 'react';
import {TemplateStructure, TemplateItem, TextTemplateItem} from '../types/templateStructure';
import {convertVariableSpansToText} from '../utils/variableUtils';
import {convertHtmlToWhatsAppFormat} from '../utils/formatUtils';

interface UseTemplateStructureProps {
    initialStructure: TemplateStructure;
    dependencies: any[];
    onChange?: (structure: TemplateStructure) => void;
}

const convertToWhatsappText = (htmlContent: string) => {
    const whatsappFormatted = convertHtmlToWhatsAppFormat(htmlContent);
    const text = convertVariableSpansToText(whatsappFormatted);
    return text.replace(/\r\n|\r|\n|\t/g, ' ').replace(/\s+/g, ' ').trim();
};
export const prepareParameterValuesForSave = (templateStructure: TemplateStructure): TextTemplateItem[] => {
    const values: TextTemplateItem[] = [];
    let currentIndex = 1;
    
    templateStructure.forEach(item => {
        if(item.type === 'inline') {
            // Add each param as a separate TextTemplateItem (only if params exist)
            if(item.params) {
                item.params.forEach(param => {
                    const textValue = convertToWhatsappText(param.value ?? '');
                    values.push({
                        index: currentIndex++,
                        type: 'text',
                        value: textValue
                    });
                });
            }
        } else if(item.type === 'text') {
            const textValue = convertToWhatsappText(item.value ?? '');
            values.push({
                index: currentIndex++,
                type: 'text',
                value: textValue
            });
        }
    });
    
    return values;
};

export function useTemplateStructure({initialStructure, dependencies, onChange}: UseTemplateStructureProps) {
    const [structure, setStructure] = useState<TemplateStructure>(initialStructure);
    const [hasChanged, setHasChanged] = useState(false);
    const pendingOnChangeRef = useRef<TemplateStructure | null>(null);

    // Reset structure when dependencies change
    useEffect(() => {
        if(initialStructure) {
            setStructure(initialStructure);
            setHasChanged(false);
        }
    }, dependencies);

    // Notify onChange after state has been updated (avoids side effects inside setState updater)
    useEffect(() => {
        if(pendingOnChangeRef.current !== null && onChange) {
            onChange(pendingOnChangeRef.current);
            pendingOnChangeRef.current = null;
        }
    }, [structure, onChange]);

    const handleItemChange = useCallback((index: number, updatedItem: TemplateItem) => {
        setStructure(prevStructure => {
            const newStructure = [...prevStructure];
            newStructure[index] = updatedItem;
            pendingOnChangeRef.current = newStructure;
            return newStructure;
        });
        setHasChanged(true);
    }, []);

    const clearEditing = useCallback(() => {
        setHasChanged(false);
    }, []);

    const clearAllContent = useCallback(() => {
        setStructure(prevStructure => {
            const cleared: TemplateStructure = prevStructure.map(item => {
                if(item.type === 'inline') {
                    return {
                        ...item,
                        params: item.params?.map(p => ({...p, value: ''})) ?? []
                    };
                }
                if(item.type === 'text') {
                    return {...item, value: ''};
                }
                return item;
            });
            pendingOnChangeRef.current = cleared;
            return cleared;
        });
        setHasChanged(true);
    }, []);

    const prepareForSave = useCallback(() => prepareParameterValuesForSave(structure), [structure]);

    return {
        structure,
        hasChanged,
        handleItemChange,
        prepareForSave,
        clearEditing,
        clearAllContent
    };
}
